import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import debounce from 'lodash.debounce';
import { Input, InputProps } from './Input';

interface DebouncedInputProps extends Omit<InputProps, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
}

export const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce: debounceTime = 500,
  ...props
}: DebouncedInputProps) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const debouncedOnChange = useMemo(
    () => debounce(onChange, debounceTime),
    [onChange, debounceTime]
  );

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setValue(value);
      debouncedOnChange(value);
    },
    [debouncedOnChange]
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (props.type === 'number' && ['e', 'E', '+', '-'].includes(e.key))
        e.preventDefault();
    },
    [props.type]
  );

  return (
    <Input
      {...props}
      value={value}
      onChange={onInputChange}
      onKeyDown={onKeyDown}
    />
  );
};
